import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';

export default function OTPInput({ value = '', onChange, numInputs = 6, disabled = false }) {
    const inputRefs = useRef([]);
    const values = value.split('').concat(Array(numInputs).fill('')).slice(0, numInputs);

    // Focus first input when value is cleared
    useEffect(() => {
        if (!value && inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    }, [value]);

    const handleChange = (index, e) => {
        const val = e.target.value;

        // Only accept single digit
        if (val.length > 1) return;

        // Only accept numbers
        if (val && !/^\d$/.test(val)) return;

        // Update the value
        const newValues = [...values];
        newValues[index] = val;
        const newValue = newValues.join('');
        onChange(newValue);

        // Auto focus next input
        if (val && index < numInputs - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index, e) => {
        // Handle backspace
        if (e.key === 'Backspace') {
            if (!values[index] && index > 0) {
                // If current is empty, go to previous and clear it
                inputRefs.current[index - 1]?.focus();
                const newValues = [...values];
                newValues[index - 1] = '';
                onChange(newValues.join(''));
            } else {
                // Clear current
                const newValues = [...values];
                newValues[index] = '';
                onChange(newValues.join(''));
            }
        }

        // Handle left arrow
        if (e.key === 'ArrowLeft' && index > 0) {
            inputRefs.current[index - 1]?.focus();
        }

        // Handle right arrow
        if (e.key === 'ArrowRight' && index < numInputs - 1) {
            inputRefs.current[index + 1]?.focus();
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, numInputs);
        onChange(pastedData);

        // Focus the next empty input or last input
        const nextIndex = Math.min(pastedData.length, numInputs - 1);
        inputRefs.current[nextIndex]?.focus();
    };

    const handleFocus = (e) => {
        e.target.select();
    };

    return (
        <Container>
            {values.map((val, index) => (
                <Input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={val}
                    onChange={(e) => handleChange(index, e)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    onFocus={handleFocus}
                    disabled={disabled}
                    autoFocus={index === 0}
                />
            ))}
        </Container>
    );
}

const Container = styled.div`
    display: flex;
    gap: 8px;
    justify-content: center;
    align-items: center;
    width: 100%;
    max-width: 360px;
    margin: 0 auto;

    @media (max-width: 480px) {
        gap: 6px;
    }

    @media (max-width: 360px) {
        gap: 4px;
    }
`;

const Input = styled.input`
    width: 50px;
    height: 50px;
    flex: 1;
    max-width: 50px;
    text-align: center;
    font-size: 24px;
    font-weight: 500;
    border: 2px solid #ddd;
    border-radius: 8px;
    background-color: white;
    color: #333;
    transition: all 0.2s;

    @media (max-width: 480px) {
        width: 45px;
        height: 45px;
        max-width: 45px;
        font-size: 20px;
    }

    @media (max-width: 360px) {
        width: 40px;
        height: 40px;
        max-width: 40px;
        font-size: 18px;
        border-radius: 6px;
    }

    &:focus {
        outline: none;
        border-color: #28a745;
        box-shadow: 0 0 0 3px rgba(40, 167, 69, 0.1);
    }

    &:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
    }

    /* Remove spinner arrows for number input */
    &::-webkit-outer-spin-button,
    &::-webkit-inner-spin-button {
        -webkit-appearance: none;
        margin: 0;
    }

    -moz-appearance: textfield;
`;